import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { recordApi } from 'app/shared-components/client-records/utils/recordApi';

// Async thunks
export const fetchSystemTemplates = createAsyncThunk(
  'recordTemplates/fetchSystemTemplates',
  async (_, { getState }) => {
    const state = getState();
    
    // Check if system templates are already loaded
    if (state.recordTemplates.systemTemplates.length > 0) {
      return state.recordTemplates.systemTemplates;
    }
    
    const response = await recordApi.getSystemTemplates();
    return response;
  }
);

export const fetchUserTemplates = createAsyncThunk(
  'recordTemplates/fetchUserTemplates',
  async (ownerKey, { getState }) => {
    const state = getState();
    
    // Simple check - if we have user templates, return them
    if (state.recordTemplates.userTemplates.length > 0) {
      return state.recordTemplates.userTemplates;
    }
    
    const response = await recordApi.getUserTemplates(ownerKey);
    return response;
  }
);

export const fetchTemplate = createAsyncThunk(
  'recordTemplates/fetchTemplate',
  async (templateId) => {
    const response = await recordApi.getTemplate(templateId);
    return { ...response, id: templateId };
  }
);

export const createTemplate = createAsyncThunk(
  'recordTemplates/createTemplate',
  async (templateData) => {
    const response = await recordApi.createTemplate(templateData);
    return response;
  }
);

export const updateTemplate = createAsyncThunk(
  'recordTemplates/updateTemplate',
  async ({ templateId, templateData }) => {
    const response = await recordApi.updateTemplate(templateId, templateData);
    return { ...response, id: templateId };
  }
);

export const deleteTemplate = createAsyncThunk(
  'recordTemplates/deleteTemplate',
  async (templateId) => {
    await recordApi.deleteTemplate(templateId);
    return templateId;
  }
);

const initialState = {
  systemTemplates: [],
  userTemplates: [],
  templatesById: {},
  currentTemplate: null,
  loading: false,
  error: null
};

const recordTemplatesSlice = createSlice({
  name: 'recordTemplates',
  initialState,
  reducers: {
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch system templates
      .addCase(fetchSystemTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.systemTemplates = action.payload || [];
        // Cache templates by ID
        action.payload?.forEach(template => {
          state.templatesById[template.id] = template;
        });
      })
      .addCase(fetchSystemTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch user templates
      .addCase(fetchUserTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.userTemplates = action.payload || [];
        // Cache templates by ID
        action.payload?.forEach(template => {
          state.templatesById[template.id] = template;
        });
      })
      .addCase(fetchUserTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch single template
      .addCase(fetchTemplate.fulfilled, (state, action) => {
        state.currentTemplate = action.payload;
        state.templatesById[action.payload.id] = action.payload;
      })
      
      // Create template
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.userTemplates.push(action.payload);
        state.templatesById[action.payload.id] = action.payload;
      })
      
      // Update template
      .addCase(updateTemplate.fulfilled, (state, action) => {
        const userIndex = state.userTemplates.findIndex(t => t.id === action.payload.id);
        if (userIndex !== -1) {
          state.userTemplates[userIndex] = action.payload;
        }
        state.templatesById[action.payload.id] = action.payload;
        if (state.currentTemplate?.id === action.payload.id) {
          state.currentTemplate = action.payload;
        }
      })
      
      // Delete template
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.userTemplates = state.userTemplates.filter(t => t.id !== action.payload);
        delete state.templatesById[action.payload];
        if (state.currentTemplate?.id === action.payload) {
          state.currentTemplate = null;
        }
      });
  }
});

export const { clearCurrentTemplate, clearError } = recordTemplatesSlice.actions;

// Selectors
export const selectSystemTemplates = (state) => state.recordTemplates.systemTemplates;
export const selectUserTemplates = (state) => state.recordTemplates.userTemplates;
export const selectAllTemplates = createSelector(
  [selectSystemTemplates, selectUserTemplates],
  (systemTemplates, userTemplates) => [...systemTemplates, ...userTemplates]
);
export const selectTemplatesById = (state) => state.recordTemplates.templatesById;
export const selectTemplateById = (templateId) => (state) => state.recordTemplates.templatesById[templateId];
export const selectCurrentTemplate = (state) => state.recordTemplates.currentTemplate;
export const selectTemplatesLoading = (state) => state.recordTemplates.loading;
export const selectTemplatesError = (state) => state.recordTemplates.error;

export default recordTemplatesSlice.reducer;